/**
 * Shadow DOM-aware query utility
 *
 * Standard querySelectorAll cannot reach into shadow roots.
 * This utility recursively searches open shadow roots to find
 * all matching elements, including those inside Web Components.
 *
 * Closed shadow roots are inaccessible by design — we skip them.
 */

/**
 * Collect all open shadow roots in an element's subtree (including the
 * element's own), depth-capped like querySelectorAllDeep. Callers that run
 * MANY selectors against one subtree (MutationWatcher, F7) walk the tree
 * once via this and then query each root directly, instead of paying the
 * full shadow walk once per selector.
 */
export function collectShadowRoots(root: Element, maxDepth = 5): ShadowRoot[] {
  const roots: ShadowRoot[] = [];

  const walk = (node: Element, depth: number) => {
    // Cap traversal depth to avoid performance issues on deep shadow trees
    if (depth > maxDepth) return;

    if (node.shadowRoot) {
      roots.push(node.shadowRoot);

      // Continue walking inside shadow root
      const children = node.shadowRoot.querySelectorAll('*');
      for (let i = 0; i < children.length; i++) {
        walk(children[i], depth + 1);
      }
    }

    // Check children of current node for shadow roots
    for (let i = 0; i < node.children.length; i++) {
      walk(node.children[i], depth + 1);
    }
  };

  walk(root, 0);
  return roots;
}

/**
 * querySelectorAll that also searches inside open shadow roots.
 */
export function querySelectorAllDeep(root: Document | Element, selector: string): Element[] {
  const results: Element[] = [];

  // Standard query on current root
  try {
    results.push(...Array.from(root.querySelectorAll(selector)));
  } catch {
    // Invalid selector — return empty
    return [];
  }

  const startPoints: Element[] = root instanceof Element
    ? [root]
    : root.body
      ? Array.from(root.body.children)
      : [];

  for (const el of startPoints) {
    for (const shadowRoot of collectShadowRoots(el)) {
      try {
        results.push(...Array.from(shadowRoot.querySelectorAll(selector)));
      } catch { /* ignore invalid selectors */ }
    }
  }

  return results;
}
